import React from 'react';

/**
 * Standard text/number/date/time/email input field.
 *
 * Usage with react-hook-form:
 *   <InputField label="Start Date *" id="startDate" type="date"
 *     register={register("startDate", { required: "Required" })}
 *     error={errors.startDate} />
 *
 * Usage controlled (no register):
 *   <InputField label="Name" type="text" value={name}
 *     onChange={e => setName(e.target.value)} placeholder="Enter name" />
 *
 * compact prop: smaller padding, rounded-xl — for inline/flex contexts.
 */

const FULL_CLS =
  'block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6';

const COMPACT_CLS =
  'rounded-xl border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 text-sm sm:leading-6';

export function InputField({
  label,
  id,
  type = 'text',
  register,        // pass result of register("field", options)
  error,           // errors.fieldName object from react-hook-form
  placeholder,
  disabled,
  hint,            // helper text below the field
  compact = false,
  className = '',  // extra classes appended to the field element
  labelClassName = '',
  wrapperClassName = '',
  ...rest          // value, onChange, onBlur, autoComplete, min, max, step, etc.
}) {
  const base = compact ? COMPACT_CLS : FULL_CLS;
  const ring = error ? 'ring-red-500 bg-red-50' : 'ring-gray-300';
  const dis = disabled ? 'bg-gray-100 cursor-not-allowed text-gray-500' : '';

  return (
    <div className={wrapperClassName || (compact ? undefined : 'w-full')}>
      {label && (
        <label
          htmlFor={id}
          className={`block text-sm font-medium text-gray-700 mb-1 ${labelClassName}`}
        >
          {label}
        </label>
      )}
      <input
        id={id}
        type={type}
        {...(register || {})}
        placeholder={placeholder}
        disabled={disabled}
        className={[base, ring, dis, className].filter(Boolean).join(' ')}
        {...rest}
      />
      {error && (
        <p className="mt-1 text-xs text-red-500">{error.message}</p>
      )}
      {hint && !error && (
        <p className="mt-1 text-xs text-gray-500">{hint}</p>
      )}
    </div>
  );
}
